class CollectionWithSlider extends HTMLElement {
  constructor() {
    super();

    this.slidesCount = +this.dataset.slides;
    this.slidesToShow = +this.dataset.slidesToShow;
    this.slidesToShowMobile = +this.dataset.slidesToShowMobile;
    this.$swiper = null;

    // Debounce: prevent multiple init/destroy calls in a short period
    this._debouncedCheck = this._debounce((resized) => this._checkAndInitSliderCore(resized), 120);

    this.init();
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => this.checkAndInitSlider());
    window.addEventListener('resize', () => this.checkAndInitSlider(true));

    // Ensure slider initialization when tab becomes visible
    this.addEventListener('sl-show', () => setTimeout(() => this.checkAndInitSlider(), 100));

    const tabPanel = this.closest('sl-tab-panel');
    if (tabPanel) {
      const mo = new MutationObserver(muts => {
        for (const m of muts) {
          if (m.type === 'attributes' && m.attributeName === 'aria-hidden') {
            if (tabPanel.getAttribute('aria-hidden') === 'false') {
              this.checkAndInitSlider();
            }
          }
        }
      });
      mo.observe(tabPanel, { attributes: true });
    }

    // Only bind the height lock handler once per tab group
    const tabGroup = this.closest('sl-tab-group');
    if (tabGroup && !tabGroup.__heightKeeperBound) {
      tabGroup.__heightKeeperBound = true;

      // Capture the event before Shoelace hides the old panel
      tabGroup.addEventListener('sl-tab-show', async (evt) => {
        const group = tabGroup;
        const targetName = evt.detail?.name || evt.target?.panel;
        const nextPanel = targetName
          ? group.querySelector(`sl-tab-panel[name="${CSS?.escape ? CSS.escape(targetName) : targetName}"]`)
          : null;

        // Lock current height into a CSS variable on the tab-group body
        this._lockGroupMinHeight(group);

        // Wait one frame to let Shoelace switch the active panel
        await this._nextFrame();

        // Wait for the new panel’s content (images/Flickity) to stabilize
        await this._waitPanelStable(nextPanel);

        // Transition to the new height and then release the lock
        this._unlockGroupMinHeight(group, nextPanel);
      }, { capture: true });
    }
  }

  // ----- Height lock helpers -----
  _lockGroupMinHeight(group) {
    // Use the currently visible panel’s height, fallback to group’s own height
    const activePanel = group.querySelector('sl-tab-panel[active]') ||
                        Array.from(group.querySelectorAll('sl-tab-panel')).find(p => p.offsetHeight > 0);
    const h = (activePanel?.getBoundingClientRect().height || group.getBoundingClientRect().height || 0);
    if (h > 0) group.style.setProperty('--tab-body-min-height', `${Math.round(h)}px`);
  }

  _unlockGroupMinHeight(group, nextPanel) {
    // Measure the new panel height and transition to it
    const newH = (nextPanel?.scrollHeight || group.scrollHeight || 0);
    requestAnimationFrame(() => {
      group.style.setProperty('--tab-body-min-height', `${Math.max(0, Math.round(newH))}px`);
      // Clean up after transition (cannot rely on ::part transitionend)
      setTimeout(() => {
        group.style.removeProperty('--tab-body-min-height');
      }, 260);
    });
  }

  async _waitPanelStable(panel) {
    // Allow layout to settle for 2 frames
    await this._nextFrame();
    await this._nextFrame();

    // If the panel has our slider, wait for it to initialize/settle
    const slider = panel?.querySelector('collection-with-slider');
    if (slider) {
      slider.checkAndInitSlider();
      if (slider.flickity) {
        // Wait until Flickity settles or fallback after 350ms
        await new Promise(res => {
          let done = false;
          const to = setTimeout(() => { if (!done) { done = true; res(); } }, 350);
          slider.flickity.once('settle', () => { if (!done) { done = true; clearTimeout(to); res(); } });
          slider.flickity.resize();
        });
      } else {
        // Wait for images or fallback after 350ms
        const ul = panel.querySelector('.collections-slider ul');
        const imgs = ul ? ul.querySelectorAll('img') : [];
        const pending = Array.from(imgs).filter(img => !img.complete);
        if (pending.length) {
          await Promise.race([
            Promise.all(pending.map(img => new Promise(r => {
              img.addEventListener('load', r, { once: true });
              img.addEventListener('error', r, { once: true });
            }))),
            new Promise(r => setTimeout(r, 350))
          ]);
        }
      }
    }
    await this._nextFrame();
  }

  _nextFrame() { return new Promise(r => requestAnimationFrame(r)); }

  // ----- Slider logic (already optimized to avoid flashing) -----
  checkAndInitSlider(resized = false) { this._debouncedCheck(resized); }

  _checkAndInitSliderCore(resized) {
    if (this.$swiper) this.destroySlider();
    this.initSlider();
    this.updateSlideImageHeight();
  }

  // If the original slides count is less than 9, double the slides
  _doubleSlidesIfNeeded(swiperEl) {
    const wrapper = swiperEl?.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    // Clean up the previous cloned slides
    for (const n of wrapper.querySelectorAll('.js-dup-slide')) {
      n.remove();
    }

    // Only count the original slides
    const originals = Array.from(wrapper.children).filter(n => !n.classList.contains('js-dup-slide'));
    const originalCount = originals.length;

    if (originalCount > 0 && originalCount < 9) {
      for (const node of originals) {
        const clone = node.cloneNode(true);
        clone.classList.add('js-dup-slide');
        clone.setAttribute('aria-hidden', 'true');
        wrapper.appendChild(clone);
      }
    }
  }

  initSlider() {
    const swiperEl = this.querySelector('.js-collection-with-slider');
    const prevEl = this.querySelector('.featured-collection--icon-previous');
    const nextEl = this.querySelector('.featured-collection--icon-next');

    const option = {
      init: false,
      draggable: true,
      crossFade: true,
      spaceBetween: 0,
      slidesPerView: 1,
      preventClicks: false,
      preventClicksPropagation: false,
      slidesOffsetAfter: 0,
      slidesOffsetBefore: 0,
      slidesPerGroup: 1,
      centeredSlides: true,
      loop: true,
      loopAdditionalSlides: 2,
      navigation: { nextEl, prevEl },
      breakpoints: {
        640:  { slidesPerView: 2 },
        990:  { slidesPerView: 3 },
        1200: { slidesPerView: 4 },
        1400: { slidesPerView: 5 },
        1800: { slidesPerView: 5 },
      },
    };

    // Double the slides if needed before creating the Swiper
    this._doubleSlidesIfNeeded(swiperEl);

    this.$swiper = new Swiper(swiperEl, option);
    this.$swiper.on('afterInit', () => this._doInit());
    this.$swiper.on('resize', () => this._doInit());
    this.$swiper.init();
  }

  _doInit() {
    const imgWrapper = this.$swiper.el.querySelector('.card__media');
    if (imgWrapper) {
      const h = imgWrapper.getBoundingClientRect().height;
      this.style.setProperty('--media-height', `${h}px`);
    }
  }

  destroySlider () {
    const swiperEl = this.querySelector('.js-collection-with-slider');
    for (const n of swiperEl?.querySelectorAll('.js-dup-slide') || []) {
      n.remove();
    }

    if (this.$swiper && typeof this.$swiper.destroy === 'function') {
      this.$swiper.destroy(true, true);
    }
    this.$swiper = null;
  }

  updateSlideImageHeight() {
    const animEl = this.querySelector('use-animate');
    if (!animEl) return;
    const height = animEl.getBoundingClientRect().height;
    if (height > 0) {
      this.style.setProperty('--slide-image-height', `${height}px`);
    }
  }

  disconnectedCallback() { this.destroySlider(); }

  _debounce(fn, wait) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
  }
}

customElements.define('collection-with-slider', CollectionWithSlider);
